import { useEffect, useState } from "react";
import useGeoServerFetch from "../../hooks/useGeoServerFetch";
import { GeoJSON, useMapEvents } from "react-leaflet";
import L, { Layer, LatLng } from "leaflet";
import { generateTooltipHTML } from "../../utils/propertyMapping";
import { layerTitleByName } from "../../global/constants/layers";
import { bindDelayedHoverTooltip } from "./tooltipDelay";
import { getGeometryField } from "../../utils/geometryField";
import "./tooltip.css";

// Icon size configuration constants
const BASE_ICON_SIZE = 24;
const MIN_ICON_SCALE = 0.5; // 50% of base size when zoomed out
const MAX_ICON_SCALE = 1.0; // 100% of base size when zoomed in
const ZOOM_THRESHOLD_MIN = 8; // Zoom level where icons are smallest
const ZOOM_THRESHOLD_MAX = 10; // Zoom level where icons reach full size
const ZOOM_INTERPOLATION_RANGE = ZOOM_THRESHOLD_MAX - ZOOM_THRESHOLD_MIN;
const DEFAULT_MAP_ZOOM = 7; // Default zoom level from MapComponent

interface GeoJSONDataProps {
  typename: string;
  workspace: string;
  style?: any;
  customIcon?: string;
  customIconSize?: number;
  zIndex?: number;
  cqlFilter?: string;
  geometryField?: string;
  minZoom?: number;
  fetchOnce?: boolean;
  afterEachFeature?: (feature: any, layer: L.Layer) => void;
  /** Quando definido, clicar numa feição chama este callback em vez de deixar
   * o evento subir para o identify genérico do mapa. */
  onFeatureClick?: (feature: any) => void;
  /** Restringe `onFeatureClick` às feições para as quais retorna true; as
   * demais deixam o clique seguir para o identify genérico. */
  isFeatureClickable?: (feature: any) => boolean;
}

export function GeoJSONData({
  typename,
  workspace,
  style,
  customIcon,
  customIconSize,
  zIndex,
  cqlFilter,
  geometryField,
  minZoom,
  fetchOnce,
  afterEachFeature,
  onFeatureClick,
  isFeatureClickable,
}: GeoJSONDataProps) {
  const [zoom, setZoom] = useState<number>(DEFAULT_MAP_ZOOM);
  const [key, setKey] = useState<number>(0);
  const [fetchKey, setFetchKey] = useState<number>(0);
  const [dataVersion, setDataVersion] = useState<number>(0);

  // Single useMapEvents handles both zoom (icon update) and move (data re-fetch)
  const map = useMapEvents({
    zoomend: (e) => {
      const newZoom = e.target.getZoom();
      setZoom(newZoom);
      setKey((prev) => prev + 1);
    },
    moveend: () => {
      if (!fetchOnce) {
        setFetchKey((prev) => prev + 1);
      }
    },
  });

  // cqlFilter alone can't be BBOX-limited by useGeoServerFetch without a
  // geometry field (cql_filter and bbox are mutually exclusive GeoServer
  // params) — callers rarely know the column name, so look it up once per
  // typename when they didn't pass one explicitly. Until it's known (or if the
  // lookup fails) no request goes out: a CQL-only request would load the whole
  // filtered layer instead of just the viewport. A failed lookup is retried on
  // the next moveend (fetchKey), so a transient error doesn't hide the layer
  // for good.
  const geoTypeName = `${workspace}:${typename}`;
  const needsGeometryLookup = !!cqlFilter && !geometryField && !fetchOnce;
  const [discoveredField, setDiscoveredField] = useState<{
    typeName: string;
    field: string;
  } | null>(null);

  const autoGeometryField =
    discoveredField?.typeName === geoTypeName
      ? discoveredField.field
      : undefined;

  useEffect(() => {
    if (!needsGeometryLookup || autoGeometryField !== undefined) return;
    let isMounted = true;
    getGeometryField(geoTypeName)
      .then((field) => {
        if (isMounted) setDiscoveredField({ typeName: geoTypeName, field });
      })
      // Already logged by getGeometryField — the layer stays unloaded until
      // the next moveend retries.
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [geoTypeName, needsGeometryLookup, autoGeometryField, fetchKey]);

  const { geoData, error, isFetching } = useGeoServerFetch(
    typename,
    workspace,
    cqlFilter,
    geometryField ?? autoGeometryField,
    fetchOnce,
    fetchKey,
    !needsGeometryLookup || autoGeometryField !== undefined
  );

  // react-leaflet v4 does not reliably update <GeoJSON> when the data prop
  // changes — force a remount every time a new response arrives.
  useEffect(() => {
    if (geoData !== null) {
      setDataVersion((v) => v + 1);
    }
  }, [geoData]);

  // Create custom pane with specified z-index
  useEffect(() => {
    if (zIndex !== undefined && map) {
      const paneName = `pane-${typename}`;
      if (!map.getPane(paneName)) {
        const pane = map.createPane(paneName);
        pane.style.zIndex = String(zIndex);
      }
    }
  }, [map, typename, zIndex]);

  // Calculate icon size based on zoom level
  const getIconSize = (): number => {
    // Zoomed out: use minimum scale
    const baseIconSize = customIconSize ?? BASE_ICON_SIZE;

    if (zoom <= ZOOM_THRESHOLD_MIN) {
      return Math.round(baseIconSize * MIN_ICON_SCALE);
    }

    // Zoomed in: use maximum scale
    if (zoom >= ZOOM_THRESHOLD_MAX) {
      return Math.round(baseIconSize * MAX_ICON_SCALE);
    }

    // In between: interpolate linearly between min and max scale
    const scaleRange = MAX_ICON_SCALE - MIN_ICON_SCALE;
    const zoomProgress = (zoom - ZOOM_THRESHOLD_MIN) / ZOOM_INTERPOLATION_RANGE;
    const scaleFactor = MIN_ICON_SCALE + zoomProgress * scaleRange;

    return Math.round(baseIconSize * scaleFactor);
  };

  const pointToLayer = customIcon
    ? (feature: any, latlng: LatLng) => {
        const iconSize = getIconSize();
        const icon = L.icon({
          iconUrl: customIcon,
          iconSize: [iconSize, iconSize],
          iconAnchor: [iconSize / 2, iconSize / 2],
          popupAnchor: [0, -iconSize / 2],
        });
        return L.marker(latlng, { icon });
      }
    : style
    ? (feature: any, latlng: LatLng) => L.circleMarker(latlng, style(feature))
    : undefined;

  const getStaticTooltipContent = (layerName: string): string | undefined => {
    if (layerName === "boundary_30km" || layerName === "boundary_100km") {
      return "distância da rede básica de energia (SIN)";
    }
    return undefined;
  };

  const onEachFeature = (feature: any, layer: Layer) => {
    let tooltipContent = getStaticTooltipContent(typename);
    let tooltipController:
      | ReturnType<typeof bindDelayedHoverTooltip>
      | undefined;

    if (!tooltipContent && feature.properties) {
      const featureColor =
        style && typeof style === "function"
          ? style(feature).fillColor || style(feature).color
          : undefined;

      tooltipContent = generateTooltipHTML(
        typename,
        feature.properties,
        featureColor,
        layerTitleByName[typename]
      );
    }

    if (tooltipContent) {
      // Determine if this is a line layer (for proximity hover)
      const isLineLayer =
        layer instanceof L.Polyline && !(layer instanceof L.Polygon);
      const getOpenLatLng = (e: L.LeafletMouseEvent) => {
        if (isLineLayer) {
          return e.latlng;
        }

        return (layer as any).getLatLng?.() ?? e.latlng;
      };

      tooltipController = bindDelayedHoverTooltip({
        layer,
        content: tooltipContent,
        tooltipOptions: {
          permanent: false,
          direction: "top",
          className: "custom-tooltip",
          offset: [0, -10],
          opacity: 1,
          sticky: isLineLayer,
        },
        isLineLayer,
        getOpenLatLng,
      });
    }

    if (onFeatureClick && (isFeatureClickable?.(feature) ?? true)) {
      layer.on("click", (e: L.LeafletMouseEvent) => {
        // Sem isso o clique também dispara o identify/popup genérico do mapa.
        L.DomEvent.stopPropagation(e);
        // O clique abre outra visão da feição; um tooltip agendado pelo hover
        // (ou pelo mouseover emulado de um toque) não pode abrir por cima dela.
        tooltipController?.cancelAndClose(() => {
          (layer as L.Path).closeTooltip();
        });
        onFeatureClick(feature);
      });
    }

    afterEachFeature?.(feature, layer);
  };

  // Só oculta enquanto não há dado algum — durante re-fetch mantém o dado anterior visível
  if (isFetching && !geoData) {
    return null;
  }

  if (error) {
    console.error(`[GeoJSONData] Failed to load ${workspace}:${typename}`);
    return null;
  }

  if (!geoData) {
    console.warn(`[GeoJSONData] No data for ${workspace}:${typename}`);
    return null;
  }

  // Validate GeoJSON structure
  if (!geoData.type || geoData.type !== "FeatureCollection") {
    console.error(
      `[GeoJSONData] Invalid GeoJSON type for ${workspace}:${typename}:`,
      geoData.type
    );
    return null;
  }

  if (!geoData.features || !Array.isArray(geoData.features)) {
    console.error(
      `[GeoJSONData] Invalid features array for ${workspace}:${typename}`
    );
    return null;
  }

  const paneName = zIndex !== undefined ? `pane-${typename}` : undefined;

  try {
    return (
      <GeoJSON
        key={`${typename}-${key}-${dataVersion}`}
        data={geoData}
        style={style}
        onEachFeature={onEachFeature}
        pointToLayer={pointToLayer}
        pane={paneName}
        // smoothFactor=0 disables geometry simplification so lines stay
        // continuous at low zoom levels (not in react-leaflet types but passed
        // through to the Leaflet GeoJSON constructor at runtime)
        {...({ smoothFactor: 0 } as any)}
      />
    );
  } catch (err) {
    console.error(
      `[GeoJSONData] Error rendering ${workspace}:${typename}:`,
      err
    );
    return null;
  }
}
